import {
  getXeroSyncStatus,
  startXeroSyncAll,
  type XeroSyncJob,
} from '@/services/api/xero'

export type XeroSyncState = XeroSyncJob

const idleState = (): XeroSyncState => ({
  status: 'idle',
  step: 'queued',
  percent: 0,
})

let state: XeroSyncState = idleState()
const listeners = new Set<(next: XeroSyncState) => void>()
let pollTimer: ReturnType<typeof setInterval> | null = null
let lastNotifiedStatus: XeroSyncState['status'] = 'idle'
let sawRunningThisSession = false
const completeListeners = new Set<(next: XeroSyncState) => void>()

function emit() {
  listeners.forEach((listener) => listener(state))
}

function apply(job: XeroSyncJob): XeroSyncState {
  return {
    ...idleState(),
    ...job,
    status: job.status ?? 'idle',
    percent: job.percent ?? 0,
    step: job.step ?? 'queued',
  }
}

async function pollOnce() {
  try {
    const job = await getXeroSyncStatus()
    state = apply(job)
    emit()
    if (state.status === 'running') sawRunningThisSession = true
    if (
      sawRunningThisSession &&
      (state.status === 'completed' || state.status === 'failed') &&
      lastNotifiedStatus !== state.status
    ) {
      completeListeners.forEach((listener) => listener(state))
    }
    lastNotifiedStatus = state.status
    if (state.status !== 'running' && pollTimer) {
      clearInterval(pollTimer)
      pollTimer = null
    }
  } catch {
    // Keep the last known state; the next tick retries.
  }
}

function startPolling() {
  if (pollTimer) return
  void pollOnce()
  pollTimer = setInterval(() => {
    void pollOnce()
  }, 2000)
}

export function subscribeXeroSync(listener: (next: XeroSyncState) => void) {
  listeners.add(listener)
  listener(state)
  return () => {
    listeners.delete(listener)
  }
}

export function onXeroSyncSettled(listener: (next: XeroSyncState) => void) {
  completeListeners.add(listener)
  return () => {
    completeListeners.delete(listener)
  }
}

export function watchXeroSyncInBackground() {
  void pollOnce().then(() => {
    if (state.status === 'running') startPolling()
  })
}

export async function startXeroSync(): Promise<XeroSyncState> {
  const job = await startXeroSyncAll()
  lastNotifiedStatus = 'running'
  sawRunningThisSession = true
  state = apply({ ...job, status: job.status ?? 'running' })
  emit()
  startPolling()
  return state
}

export function xeroSyncCompleteMessage(job: XeroSyncState) {
  if (job.status === 'failed') {
    return job.errorMessage || 'Xero sync failed.'
  }
  const r = job.result
  if (!r) return 'Xero sync completed.'
  const parts: string[] = []
  if (r.invoices?.ok) parts.push(`${r.invoices.synced ?? 0} invoices`)
  if (r.purchases?.ok) parts.push(`${r.purchases.synced ?? 0} purchases`)
  if (r.payments?.ok) parts.push(`${r.payments.synced ?? 0} payments`)
  if (r.contacts?.ok) parts.push(`${r.contacts.synced ?? 0} contacts`)
  if (r.productsServices?.ok) parts.push(`${r.productsServices.synced ?? 0} products`)
  const failed = [
    r.invoices,
    r.purchases,
    r.payments,
    r.contacts,
    r.productsServices,
  ].filter((s) => !s?.ok).length
  if (failed > 0) {
    return `Xero sync finished with ${failed} step(s) failed. Synced: ${parts.join(', ') || 'none'}.`
  }
  return `Xero sync completed — ${parts.join(', ')}.`
}
